import { Component, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { LicenseplateSettingsService } from './licenseplate-settings.service';
import { LicenseplateSettingsForm } from './licenseplate-settings.model';
import { NbDialogService, NbIconConfig, NbPopoverDirective, NbToastrService } from '@nebular/theme';
import { ConfirmationDialogComponent } from '../../../@shared/components/confirmation-dialog/confirmation-dialog.component';
import { HttpClient } from '@angular/common/http';
import { LogService } from '../../../@core';
import { NbAccessChecker } from '@nebular/security';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-licenseplate-settings',
  templateUrl: './licenseplate-settings.component.html',
  styleUrls: ['./licenseplate-settings.component.scss'],
})
export class LicenseplateSettingsComponent implements OnInit {
  formSubmitted = false;
  form!: LicenseplateSettingsForm;
  divisionId!: string;
  @ViewChild(NbPopoverDirective) popover!: NbPopoverDirective;
  accessLprSettings = false;
  private toastMessages!: string[];
  private index!: number;

  constructor(
    private dialogService: NbDialogService,
    private fb: FormBuilder,
    private toastrService: NbToastrService,
    private httpClient: HttpClient,
    private logger: LogService,
    private accessChecker: NbAccessChecker,
    private translate: TranslateService,
    private licenseplateSettingsService: LicenseplateSettingsService
  ) {
    this.accessChecker.isGranted('manage', 'accessLprSettings').subscribe((result: boolean) => {
      this.accessLprSettings = result;
    });

    this.form = this.fb.nonNullable.group(
      {
        serviceListenIpAddress: this.fb.nonNullable.control(''),
        listenForTssIpPort: this.fb.nonNullable.control(0, [Validators.required]),
        licenseWaitTime: this.fb.nonNullable.control(0, [Validators.min(0), Validators.max(120)]),
        licenseValidTime: this.fb.nonNullable.control(0, [Validators.min(0), Validators.max(120)]),
        requestWaitTime: this.fb.nonNullable.control(0, [Validators.min(0), Validators.max(5)]),
        platesLogFolder: this.fb.nonNullable.control('', [Validators.required]),
        platesRetentionDays: this.fb.nonNullable.control(0, [Validators.required]),
        useLicenseInLogFileName: this.fb.nonNullable.control(false),
        useSecondPlatesLogFolder: this.fb.nonNullable.control(false),
        secondPlatesLogFolder: this.fb.nonNullable.control(''),
        saveNoReads: this.fb.nonNullable.control(false),
        listenForCamsIpPort: this.fb.nonNullable.control(0, [Validators.required]),
        listenForUdpCamsIpPort: this.fb.nonNullable.control(0, [Validators.required]),
        targetUdpCamsIpPort: this.fb.nonNullable.control(0, [Validators.required]),
        doNotRetrieveImagesForUdpCamera: this.fb.nonNullable.control(false),
        restInterfaceCentralBaseUriListen: this.fb.nonNullable.control('', [Validators.required]),
      },
      { validators: useSecondPlatesLogFolder }
    );
  }

  get f(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  ngOnInit(): void {
    this.licenseplateSettingsService.getSettings().subscribe(
      (data) => {
        this.divisionId = data.divisionId;
        if (!this.divisionId) {
          throw new Error('No division id!');
        }
        this.patchFormValues(data);
      },
      (error) => console.log('HTTP ERROR: ', error),
      () => console.log('HTTP IS DONE ')
    );
    this.getHelpFile();

    this.translate
      .get([
        'SETTINGS.LICENSE_PLATES.TOAST.RESET_TITLE',
        'SETTINGS.LICENSE_PLATES.TOAST.RESET_MESSAGE',
        'SETTINGS.LICENSE_PLATES.TOAST.CONFIRMATION_TITLE',
        'SETTINGS.LICENSE_PLATES.TOAST.CONFIRMATION_MESSAGE',
      ])
      .subscribe((values) => {
        this.toastMessages = Object.keys(values).map((key) => values[key]);
      });
  }

  onSubmit() {
    this.formSubmitted = true;
    if (this.form.invalid) {
      return;
    }
    this.saveForm();
  }

  showToast(message: string, title: string, config: any) {
    this.index += 1;
    this.toastrService.show(message, title, config);
  }

  resetForm() {
    this.formSubmitted = false;
    const dialogConfig = {
      context: {
        title: this.toastMessages[0],
        message: this.toastMessages[1],
      },
    };
    this.dialogService
      .open(ConfirmationDialogComponent, dialogConfig)
      .onClose.subscribe((confirm: boolean) => {
        if (confirm) {
          this.form.reset();
          this.patchDefaultValues();
          this.formSubmitted = true;
          this.saveForm();
          this.logger.info('LicensePlate settings form was reset to default and saved to DB');
        }
      });
  }

  getSecondPlatesLogFolderStatus($event: boolean): void {
    if ($event) {
      // nothing
    } else {
      this.form.patchValue({
        secondPlatesLogFolder: '',
      });
      this.markAllAsTouched(this.form);
      this.form.controls['secondPlatesLogFolder'].markAsTouched();
      this.form.controls['secondPlatesLogFolder'].markAsDirty();
    }
  }

  private saveForm() {
    this.licenseplateSettingsService.saveSettings(this.prepareForApi()).subscribe((data) => {
      if (data) {
        const iconConfig: NbIconConfig = { icon: 'checkmark-outline', pack: 'eva' };
        this.showToast(this.toastMessages[3], this.toastMessages[2], {
          status: 'success',
          hasIcon: true,
          icon: iconConfig,
          duration: 5000,
          destroyByClick: true,
        });
        this.patchFormValues(data);
        this.logger.info('LicensePlate settings form was saved to DB');
      }
    });
  }

  private markFormPristine(form: FormGroup): void {
    Object.keys(form.controls).forEach((control) => {
      form.controls[control].markAsPristine();
    });
  }

  private markAllAsTouched(formGroup: FormGroup): void {
    (Object as any).values(formGroup.controls).forEach((control: any) => {
      control.markAsTouched();
      if (control.controls) {
        this.markAllAsTouched(control);
      }
    });
  }

  private patchDefaultValues() {
    this.form.patchValue({
      serviceListenIpAddress: '0',
      listenForTssIpPort: 11001,
      licenseWaitTime: 20,
      licenseValidTime: 20,
      requestWaitTime: 1,
      platesLogFolder: 'C:\\IPParking\\ParkBase\\Logs\\LPRHost\\Plates',
      platesRetentionDays: 45,
      useLicenseInLogFileName: true,
      useSecondPlatesLogFolder: false,
      secondPlatesLogFolder: '',
      saveNoReads: false,
      listenForCamsIpPort: 32000,
      listenForUdpCamsIpPort: 7050,
      targetUdpCamsIpPort: 7051,
      doNotRetrieveImagesForUdpCamera: false,
      restInterfaceCentralBaseUriListen: 'http://*:5801',
    });
  }

  private patchFormValues(d: any) {
    if (d) {
      this.form.patchValue({
        serviceListenIpAddress: d.serviceListenIpAddress,
        listenForTssIpPort: d.listenForTssIpPort,
        licenseWaitTime: d.licenseWaitTime,
        licenseValidTime: d.licenseValidTime,
        requestWaitTime: d.requestWaitTime,
        platesLogFolder: d.platesLogFolder,
        platesRetentionDays: d.platesRetentionDays,
        useLicenseInLogFileName: d.useLicenseInLogFileName,
        useSecondPlatesLogFolder: d.useSecondPlatesLogFolder,
        secondPlatesLogFolder: d.secondPlatesLogFolder,
        saveNoReads: d.saveNoReads,
        listenForCamsIpPort: d.listenForCamsIpPort,
        listenForUdpCamsIpPort: d.listenForUdpCamsIpPort,
        targetUdpCamsIpPort: d.targetUdpCamsIpPort,
        doNotRetrieveImagesForUdpCamera: d.doNotRetrieveImagesForUdpCamera,
        restInterfaceCentralBaseUriListen: d.restInterfaceCentralBaseUriListen,
      });
    }
  }

  private prepareForApi() {
    const d = this.form.controls;
    return {
      divisionId: this.divisionId,
      serviceListenIpAddress: d.serviceListenIpAddress.value.toString(),
      listenForTssIpPort: d.listenForTssIpPort.value,
      licenseWaitTime: d.licenseWaitTime.value,
      licenseValidTime: d.licenseValidTime.value,
      requestWaitTime: d.requestWaitTime.value,
      platesLogFolder: d.platesLogFolder.value,
      platesRetentionDays: d.platesRetentionDays.value,
      useLicenseInLogFileName: d.useLicenseInLogFileName.value,
      useSecondPlatesLogFolder: d.useSecondPlatesLogFolder.value,
      secondPlatesLogFolder: d.secondPlatesLogFolder.value,
      saveNoReads: d.saveNoReads.value,
      listenForCamsIpPort: d.listenForCamsIpPort.value,
      listenForUdpCamsIpPort: d.listenForUdpCamsIpPort.value,
      targetUdpCamsIpPort: d.targetUdpCamsIpPort.value,
      doNotRetrieveImagesForUdpCamera: d.doNotRetrieveImagesForUdpCamera.value,
      restInterfaceCentralBaseUriListen: d.restInterfaceCentralBaseUriListen.value,
    };
  }

  private getHelpFile() {
    // this.httpClient
    //   .get('https://vw.devipp.com:475/Help/html/nl/Help_Status_SystemStatus.html')
    //   .subscribe((result) => {
    //     console.log('result', result);
    //   });
  }
}

export const useSecondPlatesLogFolder: ValidatorFn = (
  control: AbstractControl
): ValidationErrors | null => {
  const useSecondPlatesLogFolder = control.get('useSecondPlatesLogFolder');
  const secondPlatesLogFolder = control.get('secondPlatesLogFolder');

  if (useSecondPlatesLogFolder && useSecondPlatesLogFolder.value) {
    if (secondPlatesLogFolder && secondPlatesLogFolder.value) {
      return null;
    } else {
      return { secondPlatesLogFolderUsed: true };
    }
  }

  return useSecondPlatesLogFolder && secondPlatesLogFolder && secondPlatesLogFolder.value.length > 0
    ? { secondPlatesLogFolderUsed: true }
    : null;
};
